import React, { createContext, useContext, useEffect, useRef, useState } from 'react';

export interface SensorReading {
  entity: string;
  temperature: number;
  rpm: number | null;
  status: 'OK' | 'WARNING' | 'CRITICAL';
  timestamp: string;
}

interface WSCtx {
  readings: SensorReading[];
  connected: boolean;
}

const WSContext = createContext<WSCtx>({ readings: [], connected: false });

const ENTITIES = ['Mixer_1','Mixer_3','Mixer_8','Mixer_9','Mixer_11','Mixer_12','WaterTank','FREEZER_TUNNEL_1'];

function mockReading(): SensorReading {
  const temp = +(Math.random() * 150 + 150).toFixed(1);
  const rpm  = Math.random() > 0.2 ? Math.round(Math.random() * 200 + 50) : null;
  return {
    entity: ENTITIES[Math.floor(Math.random() * ENTITIES.length)],
    temperature: temp,
    rpm,
    status: temp > 270 ? 'CRITICAL' : temp > 220 ? 'WARNING' : 'OK',
    timestamp: new Date().toISOString(),
  };
}

export const WSProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [readings, setReadings] = useState<SensorReading[]>([]);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    const tryWS = () => {
      try {
        const wsUrl = (process.env.REACT_APP_WS_URL || 'ws://localhost:5000/ws');
          const ws = new WebSocket(wsUrl);
        wsRef.current = ws;
        ws.onopen  = () => setConnected(true);
        ws.onclose = () => { setConnected(false); fallback(); };
        ws.onerror = () => { ws.close(); };
        ws.onmessage = (e) => {
          try {
            const data = JSON.parse(e.data) as SensorReading;
            setReadings(prev => [data, ...prev].slice(0, 50));
          } catch {}
        };
      } catch { fallback(); }
    };

    // Fallback: simulate readings if WS not available
    let iv: ReturnType<typeof setInterval>;
    const fallback = () => {
      setConnected(false);
      iv = setInterval(() => {
        setReadings(prev => [mockReading(), ...prev].slice(0, 50));
      }, 2000);
    };

    tryWS();
    // If WS doesn't connect within 2s, start fallback
    const timeout = setTimeout(() => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) fallback();
    }, 2000);

    return () => {
      clearTimeout(timeout);
      clearInterval(iv);
      wsRef.current?.close();
    };
  }, []);

  return (
    <WSContext.Provider value={{ readings, connected }}>
      {children}
    </WSContext.Provider>
  );
};

export const useWS = () => useContext(WSContext);
